import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Login from "./components/auth/Login";
import Signup from "./components/auth/Signup";
import MusicCreatorDashboard from "./components/musicCreator/MusicCreatorDashboard";
import MusicCreatorMyOrders from "./components/musicCreator/MusicCreatorMyOrders";
import MusicCreatorOrderDetails from "./components/musicCreator/MusicCreatorOrderDetails";
import SalesPersonDashboard from "./components/salesPerson/SalesPersonDashboard";
import AdminDashboard from "./components/admin/AdminDashboard";
import ProfilePage from "./components/profile/ProfilePage";
// import AudioUploadPage from "./components/AudioUploadPage";

import "./App.css";
import OrdersTable from "./components/admin/orderDetailsData.jsx";
import SalesPersonsTable from "./components/admin/SalesPersonsTable.js";
import MusicCreatorsTable from "./components/admin/MusicCreatorsTable.jsx";
import MusicPlayer from "./components/songUpload/MusicPlayer.jsx";
import JingleTabsPage from "./components/songUpload/JingleTabsPage.jsx";
import SongDeliveryPage from "./components/songUpload/SongDeliveryPage.jsx";
import { Toaster } from "react-hot-toast";
import CategoriesPage from "./components/samplePage/CategoriesPage.jsx";
import SongsPage from "./components/samplePage/SongsPage.jsx";
import { useDispatch, useSelector } from "react-redux";
import ShareLyrics from "./components/musicCreator/ShareLyrics.jsx";
import { logoutUser } from "./redux/reduxSlices/authSlice.js";
import CollageCreationPage from "./components/collage/CollageCreationPage.jsx";
import CollageDownloadPage from "./components/collage/CollageDownloadPage.jsx";
import ClientJingleDetailsForm from "./components/order/ClientJingleDetailsForm.jsx";
import IndividualSalesRecord from "./components/admin/IndividualSalesRecord.jsx";
import FloatingChatRoom from "./components/teamChats/FloatingChatRoom.jsx";
import AnthemPage from "./components/samplePage/AnthemPage.jsx";
import SingleSongPage from "./components/samplePage/SingleSongPage.jsx";

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    const savedUser = localStorage.getItem("currentUser");
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem("currentUser", JSON.stringify(userData));
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    // setUser(null);
    // localStorage.removeItem("currentUser");
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  // ✅ Generic Protected Route Component
  const ProtectedRoute = ({ user, allowedRole, children }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }
    if (!user) return <Navigate to="/login" replace />;
    if (allowedRole && user.role !== allowedRole)
      return <Navigate to={`/${user.role}`} replace />;
    return children;
  };

  return (
    <Router>
      <div className="App">
        <div className={`${isAuthenticated && user && "pb-20"}`}>
          <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={
                isAuthenticated && user ? (
                  <Navigate
                    to={`${user.user_type == "admin_person" ? "/admin-person" : "music_creator" ? "/music-creator" : "/sales-person"}`}
                    replace
                  />
                ) : (
                  <Login onLogin={handleLogin} />
                )
              }
            />
            {/* <Route path="/signup" element={<Signup />} /> */}

            {/* Public / semi-public pages */}
            <Route path="/watermarked-jingles/:id" element={<MusicPlayer />} />
            <Route
              path="/jingles-delivery/:id"
              element={<SongDeliveryPage />}
            />
            <Route path="/lyrics/:id" element={<ShareLyrics />} />
            <Route path="/jingle-collection" element={<CategoriesPage />} />
            <Route path="/anthem-songs" element={<AnthemPage />} />
            <Route
              path="/collage-delivery/:id"
              element={<CollageDownloadPage />}
            />
            <Route
              path="/jingle-order/:id"
              element={<ClientJingleDetailsForm />}
            />

            <Route
              path="/jingle-collection/:categoryName"
              element={<SongsPage />}
            />
            <Route
              path="/jingle-collection/:categoryName/:subCategoryName/:songIndex"
              element={<SingleSongPage />}
            />

            <Route
              path="/create-collage"
              element={
                <ProtectedRoute user={user}>
                  <CollageCreationPage user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* Protected: Music Creator */}
            <Route
              path="/music-creator"
              element={
                <ProtectedRoute user={user} allowedRole="music-creator">
                  <MusicCreatorDashboard user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/music-creator/my-orders"
              element={
                <ProtectedRoute user={user} allowedRole="music-creator">
                  <MusicCreatorMyOrders user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/my-orders/:orderId"
              element={
                <ProtectedRoute user={user}>
                  <MusicCreatorOrderDetails
                    loginUser={user}
                    onLogout={handleLogout}
                  />
                </ProtectedRoute>
              }
            />

            {/* Protected: Sales Person */}
            <Route
              path="/sales-person"
              element={
                <ProtectedRoute user={user} allowedRole="sales-person">
                  <SalesPersonDashboard users={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* Protected: Admin */}
            <Route
              path="/admin-person"
              element={
                <ProtectedRoute user={user} allowedRole="admin">
                  <AdminDashboard user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales-person-record/:salesId"
              element={
                <ProtectedRoute user={user} allowedRole="admin">
                  <IndividualSalesRecord user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin-sales-persons-data"
              element={
                <ProtectedRoute user={user} allowedRole="admin">
                  <SalesPersonsTable />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin-music-creator-data"
              element={
                <ProtectedRoute user={user} allowedRole="admin">
                  <MusicCreatorsTable />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin-order-data"
              element={
                <ProtectedRoute user={user} allowedRole="admin">
                  <OrdersTable />
                </ProtectedRoute>
              }
            />

            {/* Protected: generic logged-in (any role) */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute user={user}>
                  <ProfilePage user={user} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/audio-upload/:id"
              element={
                <ProtectedRoute user={user}>
                  <JingleTabsPage user={user} onLogout={handleLogout} />
                </ProtectedRoute>
              }
            />

            {/* Catch all */}
            <Route
              path="*"
              element={
                <Navigate
                  to={
                    isAuthenticated && user
                      ? `${user.user_type == "admin_person" ? "/admin-person" : "music_creator" ? "/music-creator" : "/sales-person"}`
                      : "/login"
                  }
                  // {`${user.user_type == "admin_person" ? "/admin-person" : "music_creator" ? "/music-creator" : "/sales-person"}`}
                  replace
                />
              }
            />
          </Routes>
        </div>
        {isAuthenticated && user && <FloatingChatRoom user={user} />}
      </div>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            duration: 3000,
            theme: {
              primary: "green",
              secondary: "black",
            },
          },
        }}
      />
    </Router>
  );
}

export default App;
